// Poki SDK wrapper
// https://sdk.poki.com/html5.html

declare global {
  interface Window {
    PokiSDK?: {
      init: () => Promise<void>;
      gameLoadingFinished: () => void;
      gameplayStart: () => void;
      gameplayStop: () => void;
      commercialBreak: (beforeAd?: () => void) => Promise<void>;
      rewardedBreak: (options?: {
        size?: "small" | "medium" | "large";
        onStart?: () => void;
      }) => Promise<boolean>;
    };
  }
}

const POKI_SDK_URL = "https://game-cdn.poki.com/scripts/v2/poki-sdk.js";

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

class PokiSDKWrapper {
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        // Dynamically load the SDK
        await loadScript(POKI_SDK_URL);

        if (window.PokiSDK) {
          await window.PokiSDK.init();
          console.log("Poki SDK initialized");
        }
      } catch (e) {
        console.log("Poki SDK not available, continuing without it");
      }
      this.initialized = true;
    })();

    return this.initPromise;
  }

  gameLoadingFinished(): void {
    if (window.PokiSDK) {
      window.PokiSDK.gameLoadingFinished();
    }
  }

  gameplayStart(): void {
    if (window.PokiSDK) {
      window.PokiSDK.gameplayStart();
    }
  }

  gameplayStop(): void {
    if (window.PokiSDK) {
      window.PokiSDK.gameplayStop();
    }
  }

  async commercialBreak(onPause?: () => void): Promise<void> {
    if (window.PokiSDK) {
      await window.PokiSDK.commercialBreak(onPause);
    }
  }

  async rewardedBreak(
    size: "small" | "medium" | "large" = "medium",
  ): Promise<boolean> {
    if (window.PokiSDK) {
      return window.PokiSDK.rewardedBreak({ size });
    }
    return false;
  }
}

export const pokiSDK = new PokiSDKWrapper();
